/**
 * Países agrupados por continente, para la cascada del alta de candidato.
 *
 * **Se guardan CÓDIGOS ISO de dos letras, no nombres.** El nombre de un país
 * depende del idioma y de la época («Birmania» / «Myanmar», «Turquía» /
 * «Türkiye»); el código no. Guardar el nombre es lo que dejó el campo de
 * nacionalidad con tres formatos mezclados a la vez —países en español,
 * gentilicios en inglés y minúsculas sueltas— y sin forma de agrupar nada.
 *
 * Los nombres para pantalla NO viven aquí: los da `Intl.DisplayNames` en el
 * idioma activo, así que la lista se traduce sola y no hay que mantener 200
 * etiquetas por idioma.
 *
 * Por qué no se reutiliza `GCC_LOCATIONS`: ese catálogo es de **ubicación** (129
 * sitios del Golfo, «País — Ciudad») y esto es **nacionalidad**. Son cosas
 * distintas — una persona nepalí vive en Dubái— y mezclarlas haría imposible
 * responder a cualquiera de las dos preguntas.
 *
 * Cinco continentes, con América como uno solo: es la agrupación que la clienta
 * usa para leer su cartera, no una clasificación geográfica.
 */

export const CONTINENTS = ['africa', 'america', 'asia', 'europe', 'oceania'] as const;
export type Continent = (typeof CONTINENTS)[number];

/** Los códigos ISO 3166-1 alfa-2 de cada continente. */
export const COUNTRIES_BY_CONTINENT: Record<Continent, readonly string[]> = {
	africa: [
		'DZ',
		'AO',
		'BJ',
		'BW',
		'BF',
		'BI',
		'CV',
		'CM',
		'CF',
		'TD',
		'KM',
		'CD',
		'CG',
		'CI',
		'DJ',
		'EG',
		'GQ',
		'ER',
		'SZ',
		'ET',
		'GA',
		'GM',
		'GH',
		'GN',
		'GW',
		'KE',
		'LS',
		'LR',
		'LY',
		'MG',
		'MW',
		'ML',
		'MR',
		'MU',
		'MA',
		'MZ',
		'NA',
		'NE',
		'NG',
		'RW',
		'ST',
		'SN',
		'SC',
		'SL',
		'SO',
		'ZA',
		'SS',
		'SD',
		'TZ',
		'TG',
		'TN',
		'UG',
		'ZM',
		'ZW',
	],
	america: [
		'AG',
		'AR',
		'BS',
		'BB',
		'BZ',
		'BO',
		'BR',
		'CA',
		'CL',
		'CO',
		'CR',
		'CU',
		'DM',
		'DO',
		'EC',
		'SV',
		'GD',
		'GT',
		'GY',
		'HT',
		'HN',
		'JM',
		'MX',
		'NI',
		'PA',
		'PY',
		'PE',
		'KN',
		'LC',
		'VC',
		'SR',
		'TT',
		'US',
		'UY',
		'VE',
		'PR',
	],
	asia: [
		'AF',
		'AM',
		'AZ',
		'BH',
		'BD',
		'BT',
		'BN',
		'KH',
		'CN',
		'CY',
		'GE',
		'IN',
		'ID',
		'IR',
		'IQ',
		'IL',
		'JP',
		'JO',
		'KZ',
		'KW',
		'KG',
		'LA',
		'LB',
		'MY',
		'MV',
		'MN',
		'MM',
		'NP',
		'KP',
		'OM',
		'PK',
		'PS',
		'PH',
		'QA',
		'SA',
		'SG',
		'KR',
		'LK',
		'SY',
		'TW',
		'TJ',
		'TH',
		'TL',
		'TR',
		'TM',
		'AE',
		'UZ',
		'VN',
		'YE',
		'HK',
		'MO',
	],
	europe: [
		'AL',
		'AD',
		'AT',
		'BY',
		'BE',
		'BA',
		'BG',
		'HR',
		'CZ',
		'DK',
		'EE',
		'FI',
		'FR',
		'DE',
		'GR',
		'HU',
		'IS',
		'IE',
		'IT',
		'XK',
		'LV',
		'LI',
		'LT',
		'LU',
		'MT',
		'MD',
		'MC',
		'ME',
		'NL',
		'MK',
		'NO',
		'PL',
		'PT',
		'RO',
		'RU',
		'SM',
		'RS',
		'SK',
		'SI',
		'ES',
		'SE',
		'CH',
		'UA',
		'GB',
		'VA',
	],
	oceania: ['AU', 'FJ', 'KI', 'MH', 'FM', 'NR', 'NZ', 'PW', 'PG', 'WS', 'SB', 'TO', 'TV', 'VU'],
};

/** Todos los códigos, sin agrupar. */
export const COUNTRY_CODES = CONTINENTS.flatMap((continent) => COUNTRIES_BY_CONTINENT[continent]);

/**
 * En qué continente cae un país. `null` si el código no está en el catálogo —
 * que es lo que pasa con los códigos retirados (`SU`, `YU`, `ZR`…) y con los
 * territorios que no ofrecemos.
 */
export function continentOf(countryCode: string | null | undefined): Continent | null {
	if (!countryCode) return null;
	const code = countryCode.toUpperCase();
	return CONTINENTS.find((continent) => COUNTRIES_BY_CONTINENT[continent].includes(code)) ?? null;
}

/**
 * Región de un empleo, para el cruce de vacantes (05/10).
 *
 * No reutiliza el continente de la nacionalidad: América sigue siendo UN continente
 * en el perfil, y aquí Estados Unidos y Canadá no cuentan como Latinoamérica.
 * Europa incluye España y Portugal. Un código desconocido no es ninguna región.
 */
export type ExperienceRegion = 'europe' | 'latin_america';

const NOT_LATIN_AMERICA = new Set(['US', 'CA']);

export function experienceRegionOf(countryCode: string | null | undefined): ExperienceRegion | null {
	if (!countryCode) return null;
	const code = countryCode.toUpperCase();
	if ((COUNTRIES_BY_CONTINENT.europe as readonly string[]).includes(code)) return 'europe';
	if ((COUNTRIES_BY_CONTINENT.america as readonly string[]).includes(code) && !NOT_LATIN_AMERICA.has(code)) {
		return 'latin_america';
	}
	return null;
}

/**
 * El nombre del país en el idioma pedido. Devuelve el propio código si no se
 * reconoce, que es el comportamiento de `Intl` y sirve de aviso visible.
 */
export function countryName(countryCode: string, locale = 'es'): string {
	return new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode.toUpperCase()) ?? countryCode;
}
